import type {
  DataEvent,
  DataEventChannelPurchase,
  DataEventChannelSetPurchase,
  DataEventEpisodePurchase,
  DataEventLiveEventPurchase,
  DataEventPartnerProductPurchase,
  DataEventSeasonPurchase,
  DataEventSubscriptionPurchase,
  DataEventTvShowPurchase,
  DataEventTvShowSetPurchase,
  DataEventVodPurchase,
  DataEventVodSetPurchase,
} from '~/core/sse'
import type { Purchased, SseLoadedInfo } from './index.h'

import { combine, createEvent, createStore, sample, split } from 'effector'
import { DataEventType, model as sse } from '~/core/sse'
import { model as session } from '~/entities/session'
import { aye, nay } from '~/shared/helpers'
import {
  getShowBundleInfoFx,
  getTvBundleInfoFx,
  getTvChannelsByIdFx,
  getTvShowBaseFx,
  getTvShowEpisodeFx,
  getTvShowEpisodeWithoutSeasonFx,
  getTvShowSeasonByIdBaseFx,
  getVodBundleInfoFx,
  getVodFx,
} from './api'
import { adjust, typeOf, updateMap } from './lib'

export const addPurchase = createEvent<DataEvent['data'] & SseLoadedInfo>()

export const $purchased = createStore<Purchased>({
  channel: new Map(),
  channelSet: new Map(),
  episode: new Map(),
  liveEvent: new Map(),
  partnerProduct: new Map(),
  season: new Map(),
  subscription: new Map(),
  tvShow: new Map(),
  tvShowSet: new Map(),
  vod: new Map(),
  vodSet: new Map(),
})
  .reset(session.$session)
  .on(addPurchase, (purchased, { type, itemId, purchaseInfo }) => {
    const push = updateMap(purchased)
    switch (type) {
      case DataEventType.liveEventPurchase:
        return push('liveEvent', itemId)
      case DataEventType.partnerProductPurchase:
        return push('partnerProduct', itemId)
      case DataEventType.subscriptionPurchase:
        return push('subscription', itemId)
      case DataEventType.channelPurchase:
        return push('channel', itemId, purchaseInfo)
      case DataEventType.channelSetPurchase:
        return push('channelSet', itemId, purchaseInfo)
      case DataEventType.episodePurchase:
        return push('episode', itemId, purchaseInfo)
      case DataEventType.seasonPurchase:
        return push('season', itemId, purchaseInfo)
      case DataEventType.tvShowPurchase:
        return push('tvShow', itemId, purchaseInfo)
      case DataEventType.tvShowSetPurchase:
        return push('tvShowSet', itemId, purchaseInfo)
      case DataEventType.vodPurchase:
        return push('vod', itemId, purchaseInfo)
      case DataEventType.vodSetPurchase:
        return push('vodSet', itemId, purchaseInfo)
      default:
        return purchased
    }
  })

// prettier-ignore
const {
  vodSetPurchase,
  vodPurchase,
  channelPurchase,
  channelSetPurchase,
  episodePurchase,
  seasonPurchase,
  tvShowPurchase,
  tvShowSetPurchase,
  liveEventPurchase,
  partnerProductPurchase,
  subscriptionPurchase,
} = split(sse.dataMessage, {
  vodSetPurchase: typeOf<DataEventVodSetPurchase>(DataEventType.vodSetPurchase),
  vodPurchase: typeOf<DataEventVodPurchase>(DataEventType.vodPurchase),
  channelPurchase: typeOf<DataEventChannelPurchase>(DataEventType.channelPurchase),
  channelSetPurchase: typeOf<DataEventChannelSetPurchase>(DataEventType.channelSetPurchase),
  episodePurchase: typeOf<DataEventEpisodePurchase>(DataEventType.episodePurchase),
  seasonPurchase: typeOf<DataEventSeasonPurchase>(DataEventType.seasonPurchase),
  tvShowPurchase: typeOf<DataEventTvShowPurchase>(DataEventType.tvShowPurchase),
  tvShowSetPurchase: typeOf<DataEventTvShowSetPurchase>(DataEventType.tvShowSetPurchase),
  liveEventPurchase: typeOf<DataEventLiveEventPurchase>(DataEventType.liveEventPurchase),
  partnerProductPurchase: typeOf<DataEventPartnerProductPurchase>(DataEventType.partnerProductPurchase),
  subscriptionPurchase: typeOf<DataEventSubscriptionPurchase>(DataEventType.subscriptionPurchase),
})

//
// LiveEvent, Partner Product and Subscription purchases
// don't require additional info, so we can add them to the store immediately
//

sample({
  clock: [
    liveEventPurchase,
    partnerProductPurchase,
    subscriptionPurchase,
    channelPurchase,
    channelSetPurchase,
  ],
  target: addPurchase,
})

//
// VOD Set purchase
// requires fetching bundle info to get purchase info
//

sample({
  clock: vodSetPurchase,
  fn: (data) => ({ bundleId: data.itemId }),
  target: getVodBundleInfoFx,
})

sample({
  clock: getVodBundleInfoFx.doneData,
  fn: (bundle) => {
    return {
      type: DataEventType.vodSetPurchase,
      itemId: bundle.id,
      purchaseInfo: bundle.purchaseInfo,
    } as DataEventVodSetPurchase
  },
  target: addPurchase,
})

//
// VOD purchase
// requires fetching VOD info to get purchase info
//

sample({
  clock: vodPurchase,
  fn: ({ itemId }) => ({ vodId: itemId }),
  target: getVodFx,
})

sample({
  clock: getVodFx.doneData,
  fn: (vod) => {
    return {
      type: DataEventType.vodPurchase,
      itemId: vod.id,
      purchaseInfo: vod.purchaseInfo,
    } as DataEventVodPurchase
  },
  target: addPurchase,
})

//
// Channel Set purchase
// requires fetching bundle info to get purchase info
//

sample({
  clock: channelSetPurchase,
  fn: ({ itemId }) => ({ bundleId: itemId }),
  target: getTvBundleInfoFx,
})

sample({
  clock: getTvBundleInfoFx.doneData,
  fn: (bundle) => {
    return {
      type: DataEventType.channelSetPurchase,
      itemId: bundle.id,
      purchaseInfo: bundle.purchaseInfo,
    } as DataEventChannelSetPurchase
  },
  target: addPurchase,
})

//
// Channel purchase
// requires fetching channel info to get purchase info
//

sample({
  clock: channelPurchase,
  fn: ({ itemId }) => ({ ids: [itemId] }),
  target: getTvChannelsByIdFx,
})

sample({
  clock: getTvChannelsByIdFx.doneData,
  filter: (data) => data.length > 0,
  fn: ([channel]) => {
    return {
      type: DataEventType.channelPurchase,
      itemId: channel.id,
      purchaseInfo: channel.purchaseInfo,
    } as DataEventChannelPurchase
  },
  target: addPurchase,
})

//
// TV Show Set purchase
// requires fetching bundle info to get purchase info
//

sample({
  clock: tvShowSetPurchase,
  fn: ({ itemId }) => ({ bundleId: itemId }),
  target: getShowBundleInfoFx,
})

sample({
  clock: getShowBundleInfoFx.doneData,
  fn: (bundle) => {
    return {
      type: DataEventType.tvShowSetPurchase,
      itemId: bundle.id,
      purchaseInfo: bundle.purchaseInfo,
    } as DataEventTvShowSetPurchase
  },
  target: addPurchase,
})

//
// TV Show purchase
// requires fetching show info to get purchase info
//

sample({
  clock: tvShowPurchase,
  fn: ({ itemId }) => ({ tvShowId: itemId }),
  target: getTvShowBaseFx,
})

sample({
  clock: getTvShowBaseFx.doneData,
  filter: (data) => data.length > 0,
  fn: ([show]) => {
    return {
      type: DataEventType.tvShowPurchase,
      itemId: show.id,
      purchaseInfo: show.purchaseInfo,
    } as DataEventTvShowPurchase
  },
  target: addPurchase,
})

//
// TV Show Season purchase
// requires fetching season info to get purchase info
//

sample({
  clock: seasonPurchase,
  fn: ({ itemId, metadata }) => ({
    seasonId: itemId,
    tvShowId: metadata.tvShowId,
  }),
  target: getTvShowSeasonByIdBaseFx,
})

sample({
  clock: getTvShowSeasonByIdBaseFx.done,
  fn: ({ params, result: season }) => {
    return {
      type: DataEventType.seasonPurchase,
      itemId: season.id,
      purchaseInfo: season.purchaseInfo,
      metadata: {
        tvShowId: params.tvShowId,
      },
    } as DataEventSeasonPurchase
  },
  target: addPurchase,
})

//
// TV Show Episode purchase
// requires fetching episode info to get purchase info
//

// if season is not provided, get episode without season
sample({
  clock: episodePurchase,
  filter: ({ metadata }) => nay(metadata.seasonId),
  fn: ({ itemId, metadata }) => ({
    episodeId: itemId,
    tvShowId: metadata.tvShowId,
  }),
  target: getTvShowEpisodeWithoutSeasonFx,
})

// if season is provided, get episode with season
sample({
  clock: episodePurchase,
  filter: ({ metadata }) => aye(metadata.seasonId),
  fn: ({ itemId, metadata }) => ({
    episodeId: itemId,
    tvShowId: metadata.tvShowId,
    seasonId: metadata.seasonId!,
  }),
  target: getTvShowEpisodeFx,
})

sample({
  clock: [getTvShowEpisodeFx.done, getTvShowEpisodeWithoutSeasonFx.done],
  fn: ({ params, result: episode }) => {
    return {
      type: DataEventType.episodePurchase,
      itemId: episode.id,
      purchaseInfo: episode.purchaseInfo,
      metadata: {
        tvShowId: params.tvShowId,
        seasonId: 'seasonId' in params ? params.seasonId : undefined,
      },
    } as DataEventEpisodePurchase
  },
  target: addPurchase,
})

//
//
//

export const $channelIds = $purchased.map((p) => p.channel)
export const $channelSetIds = $purchased.map((p) => p.channelSet)
export const $episodeIds = $purchased.map((p) => p.episode)
export const $liveEventIds = $purchased.map((p) => p.liveEvent)
export const $partnerProductIds = $purchased.map((p) => p.partnerProduct)
export const $seasonIds = $purchased.map((p) => p.season)
export const $subscriptionIds = $purchased.map((p) => p.subscription)
export const $tvShowIds = $purchased.map((p) => p.tvShow)
export const $tvShowSetIds = $purchased.map((p) => p.tvShowSet)
export const $vodIds = $purchased.map((p) => p.vod)
export const $vodSetIds = $purchased.map((p) => p.vodSet)

const $s = session.$session
export const $channel = combine($purchased, $s, adjust('channel', 'channelSet'))
export const $channelSet = combine(
  $purchased,
  $s,
  adjust('channelSet', 'channelSet')
)
export const $episode = combine($purchased, $s, adjust('episode', 'tvShowSet'))
export const $liveEvent = combine($purchased, $s, adjust('liveEvent'))
export const $partnerProduct = combine($purchased, $s, adjust('partnerProduct'))
export const $season = combine($purchased, $s, adjust('season', 'tvShowSet'))
export const $subscription = combine($purchased, $s, adjust('subscription'))
export const $tvShow = combine($purchased, $s, adjust('tvShow', 'tvShowSet'))
export const $tvShowSet = combine(
  $purchased,
  $s,
  adjust('tvShowSet', 'tvShowSet')
)
export const $vod = combine($purchased, $s, adjust('vod', 'vodSet'))
export const $vodSet = combine($purchased, $s, adjust('vodSet', 'vodSet'))

// generic adjust for any content type
export const $adjust = combine(
  $purchased,
  $s,
  (purchased, session) => (type: keyof Purchased, bundle?: keyof Purchased) =>
    adjust(type, bundle)(purchased, session)
)
